import React, { useEffect, useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  LogOut,
  CheckCircle2,
  CalendarDays,
  Clock,
  Wallet,
  Briefcase,
  MessageSquare,
} from "lucide-react";
import Calendar from "./Calender";
import { useGlobal } from "../context/GlobalContext";

const LogoutContent = ({ onClose, onConfirm }) => (
  <Modal.Body className="p-0">
    <div className="text-center px-4 pt-4 pb-3">
      <div
        className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
        style={{
          width: 64,
          height: 64,
          background: "linear-gradient(135deg, #ff4d4d20, #ff000015)",
          border: "2px solid #ff4d4d30",
        }}
      >
        <LogOut size={26} className="text-danger" />
      </div>
      <h5 className="fw-bold text-dark mb-1">Log Out</h5>
      <p className="text-muted fs-14 mb-0">
        Are you sure you want to log out of your account?
      </p>
    </div>

    <div className="d-flex gap-2 px-4 pb-4 pt-2">
      <button
        className="btn flex-fill py-2 fw-semibold fs-14 rounded-3"
        style={{ background: "#f4f6fb", color: "#555", border: "none" }}
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        className="btn flex-fill py-2 fw-semibold fs-14 rounded-3 text-white"
        style={{
          background: "linear-gradient(135deg, #ff4d4d, #e60000)",
          border: "none",
          boxShadow: "0 4px 12px rgba(230,0,0,0.25)",
        }}
        onClick={onConfirm}
      >
        Log Out
      </button>
    </div>
  </Modal.Body>
);

const JobSubmittedContent = ({ onConfirm, amount }) => {
  const hasAmount = amount != null && Number(amount) > 0;
  return (
    <Modal.Body className="p-0">
      <div className="text-center px-4 pt-4 pb-3">
        <div
          className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: 64,
            height: 64,
            background: "linear-gradient(135deg, #22c55e20, #16a34a15)",
            border: "2px solid #22c55e30",
          }}
        >
          <CheckCircle2 size={28} className="text-success" />
        </div>
        <h5 className="fw-bold text-dark mb-1">Job Submitted Successfully</h5>
        <p className="text-muted fs-14 mb-0">
          You have completed your job. The admin will review your work and
          points will be credited to your account shortly.
        </p>
      </div>

      {hasAmount && (
        <div className="px-4 pb-1">
          <div className="ej-collect-box">
            <div className="ej-collect-head">
              <span className="ej-collect-icon">
                <Wallet size={16} />
              </span>
              <span className="ej-collect-label">Amount to collect</span>
            </div>
            <div className="ej-collect-amount">
              ${Number(amount).toLocaleString()}
            </div>
            <p className="ej-collect-note mb-0">
              Please collect this amount from the customer before you leave the
              property.
            </p>
          </div>
        </div>
      )}

      <div className="px-4 pb-4 pt-3">
        <button
          className="btn w-100 py-2 fw-semibold fs-14 rounded-3 text-white"
          style={{
            background: "linear-gradient(135deg, #22c55e, #16a34a)",
            border: "none",
            boxShadow: "0 4px 12px rgba(22,163,74,0.25)",
          }}
          onClick={onConfirm}
        >
          {hasAmount ? "Payment Collected · Go to Home" : "Go to Home"}
        </button>
      </div>
    </Modal.Body>
  );
};

const JOB_STATUS_STYLES = {
  pending: { bg: "#fef3c7", color: "#b45309" },
  completed: { bg: "#dcfce7", color: "#16a34a" },
  started: { bg: "#dbeafe", color: "#2563eb" },
  "in progress": { bg: "#dbeafe", color: "#2563eb" },
};
const jobStatusStyle = (s) =>
  JOB_STATUS_STYLES[(s || "").toLowerCase()] || { bg: "#e2e8f0", color: "#475569" };

// Converts "MM/DD/YYYY hh:mm AM|PM" into a 24-hour "HH:MM" value.
const parseScheduleTime = (scheduleDateTime) => {
  if (!scheduleDateTime) return "";
  const parts = scheduleDateTime.trim().split(/\s+/);
  const timePart = parts[1];
  const meridiem = (parts[2] || "").toUpperCase();
  if (!timePart) return "";
  let [h, m] = timePart.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  if (meridiem === "PM" && h < 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// 24-hour "HH:MM" -> { h: "01".."12", m: "00".."59", mer: "AM"|"PM" }
const split12 = (val) => {
  if (!val) return { h: "", m: "", mer: "AM" };
  const [H, M] = val.split(":").map((n) => parseInt(n, 10));
  const mer = H >= 12 ? "PM" : "AM";
  let h12 = H % 12;
  if (h12 === 0) h12 = 12;
  return { h: String(h12).padStart(2, "0"), m: String(M).padStart(2, "0"), mer };
};

// { h12, m, mer } -> 24-hour "HH:MM" (empty until both hour and minute are set).
const join24 = (h12, m, mer) => {
  if (!h12 || m === "") return "";
  let h = parseInt(h12, 10) % 12;
  if (mer === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${m}`;
};

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// 12-hour time picker (HH : MM AM/PM) that reports its value as a 24-hour "HH:MM" string.
const TimeSelect = ({ value, onChange, disabled }) => {
  const { h, m, mer } = split12(value);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const emit = (nh, nm, nmer) => onChange(join24(nh, nm, nmer));

  return (
    <div className="lmt-time-field-wrap" ref={ref}>
      <div
        className={`form-control input-tags d-flex justify-content-between align-items-center cursor-pointer ${
          open ? "border-primary" : ""
        } ${disabled ? "lmt-time-disabled" : ""}`}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className={value ? "" : "text-muted"}>
          {value ? `${h}:${m} ${mer}` : "HH : MM"}
        </span>
        <Clock size={16} className="text-muted" />
      </div>

      {open && !disabled && (
        <div className="lmt-time-pop shadow-lg bg-white">
          <div className="lmt-time-col">
            {HOURS_12.map((x) => (
              <button
                type="button"
                key={x}
                className={`lmt-time-opt${h === x ? " active" : ""}`}
                onClick={() => emit(x, m || "00", mer)}
              >
                {x}
              </button>
            ))}
          </div>
          <div className="lmt-time-col">
            {MINUTES_60.map((x) => (
              <button
                type="button"
                key={x}
                className={`lmt-time-opt${m === x ? " active" : ""}`}
                onClick={() => emit(h || "12", x, mer)}
              >
                {x}
              </button>
            ))}
          </div>
          <div className="lmt-time-col lmt-time-col--mer">
            {["AM", "PM"].map((x) => (
              <button
                type="button"
                key={x}
                className={`lmt-time-opt${mer === x ? " active" : ""}`}
                onClick={() => emit(h || "12", m || "00", x)}
              >
                {x}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LogManualTimeContent = ({ show, onClose, onSubmit }) => {
  const { getPendingJobDetails } = useGlobal();
  const initialFormData = {
    date: new Date().toLocaleDateString("en-CA"),
    fromTime: "",
    toTime: "",
    jobPid: "",
    remark: "",
    title: null,
  };
  const [formData, setFormData] = useState(initialFormData);
  const [showCalendar, setShowCalendar] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Fetch pending jobs for a date and populate the selectable list.
  const fetchPendingJobs = async (date) => {
    setLoadingJobs(true);
    setJobs([]);
    setFormData((prev) => ({ ...prev, jobPid: "" }));
    const res = await getPendingJobDetails(date);
    console.log("Pending job details:", res);
    setJobs(Array.isArray(res?.data) ? res.data : []);
    setLoadingJobs(false);
  };

  useEffect(() => {
    if (show) {
      // Modal opened — load pending jobs for the default (today's) date.
      fetchPendingJobs(initialFormData.date);
    } else {
      setFormData(initialFormData);
      setShowCalendar(false);
      setJobs([]);
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // If From Time moves to/after the current To Time, clear the now-invalid To Time.
      if (name === "fromTime" && prev.toTime && prev.toTime <= value) {
        updated.toTime = "";
      }

      return updated;
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setShowCalendar(false);
  };

  const handleDateChange = (date) => {
    let dateStr = "";
    if (date instanceof Date) {
      dateStr = date.toLocaleDateString("en-CA");
    } else {
      dateStr = date.replace(/\//g, "-");
    }
    setFormData((prev) => ({ ...prev, date: dateStr }));
    setShowCalendar(false);
    // Date changed — reload pending jobs for the newly selected date.
    fetchPendingJobs(dateStr);
  };

  // Selecting a job also prefills From Time with the job's scheduled time.
  const handleSelectJob = (job) => {
    const fromTime = parseScheduleTime(job.schedule_date_time);
    setFormData((prev) => {
      const updated = { ...prev, jobPid: job.job_pid, title: job.title, fromTime };
      // Clear To Time if it's no longer after the prefilled From Time.
      if (fromTime && prev.toTime && prev.toTime <= fromTime) {
        updated.toTime = "";
      }
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fromTime || !formData.toTime) {
      toast.error("Please select both From and To time");
      return;
    }
    if (formData.toTime <= formData.fromTime) {
      toast.error("To Time must be after From Time");
      return;
    }
    onSubmit && onSubmit(formData);
    resetForm();
    onClose();
  };

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0 ">
        <Modal.Title className="fw-bold text-primary fs-17">
          Log Manual Time
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        <Form onSubmit={handleSubmit}>
          <div className="mb-4 position-relative">
            <label className="form-label d-flex align-items-center">
              <CalendarDays size={16} className="me-2 text-primary" />
              Select Date
            </label>
            <div
              className={`form-control input-tags d-flex justify-content-between align-items-center cursor-pointer ${
                showCalendar ? "border-primary" : ""
              }`}
              onClick={() => setShowCalendar(!showCalendar)}
            >
              <span>{formData.date || "Select Date"}</span>
              <CalendarDays size={18} className="text-muted" />
            </div>

            {showCalendar && (
              <div
                className="calendar-popover shadow-lg position-absolute w-100 bg-white"
                style={{ zIndex: 1050, top: "100%" }}
              >
                <Calendar
                  mode="manual"
                  value={formData.date.replace(/-/g, "/")}
                  onDateChange={handleDateChange}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label d-flex align-items-center justify-content-between">
              <span className="d-flex align-items-center">
                <Briefcase size={16} className="me-2 text-primary" />
                Select Job
                {!formData.jobPid && <span className="text-danger ms-1">*</span>}
              </span>
              {jobs.length > 0 && (
                <span className="fs-12 text-muted fw-normal">
                  {jobs.length} pending
                </span>
              )}
            </label>

            {loadingJobs ? (
              <div className="lmt-jobs-empty text-muted">Loading jobs…</div>
            ) : jobs.length === 0 ? (
              <div className="lmt-jobs-empty text-muted">
                No pending jobs for this date.
              </div>
            ) : (
              <div className="lmt-jobs-list">
                {jobs.map((job) => {
                  const selected = formData.jobPid === job.job_pid;
                  const st = jobStatusStyle(job.status);
                  return (
                    <button
                      type="button"
                      key={job.job_pid}
                      className={`lmt-job-card${selected ? " selected" : ""}`}
                      onClick={() => handleSelectJob(job)}
                    >
                      <span className="lmt-job-radio">
                        {selected && <CheckCircle2 size={18} />}
                      </span>
                      <span className="lmt-job-info">
                        <span className="lmt-job-title">{job.title}</span>
                        <span className="lmt-job-meta">
                          {job.schedule_date_time && (
                            <span className="lmt-job-time">
                              <Clock size={12} className="me-1" />
                              {job.schedule_date_time}
                            </span>
                          )}
                          <span
                            className="lmt-job-status"
                            style={{ background: st.bg, color: st.color }}
                          >
                            {job.status}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="row mb-4">
            <div className="col-6">
              <label className="form-label d-flex align-items-center">
                <Clock size={15} className="me-2 text-primary" />
                From Time
                {!formData.fromTime && (
                  <span className="text-danger ms-1">*</span>
                )}
              </label>
              <TimeSelect
                value={formData.fromTime}
                onChange={(v) =>
                  handleChange({ target: { name: "fromTime", value: v } })
                }
              />
            </div>
            <div className="col-6">
              <label className="form-label d-flex align-items-center">
                <Clock size={15} className="me-2 text-primary" />
                To Time
                {!formData.toTime && (
                  <span className="text-danger ms-1">*</span>
                )}
              </label>
              <TimeSelect
                value={formData.toTime}
                disabled={!formData.fromTime}
                onChange={(v) =>
                  handleChange({ target: { name: "toTime", value: v } })
                }
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label d-flex align-items-center">
              <MessageSquare size={15} className="me-2 text-primary" />
              Remark
            </label>
            <textarea
              name="remark"
              className="form-control input-tags"
              placeholder="Enter any notes..."
              rows="3"
              value={formData.remark}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="d-flex gap-3 mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary w-100 py-2 fw-semibold"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-secondary w-100 py-2 fw-semibold"
              disabled={!formData.jobPid}
            >
              Submit
            </button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};

const AppModal = ({ type, show, onClose, onConfirm, onSubmit, amount }) => {
  if (type === "log-manual-time") {
    return (
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        className=""
      >
        <LogManualTimeContent
          show={show}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
    >
      {type === "logout" && (
        <LogoutContent onClose={onClose} onConfirm={onConfirm} />
      )}
      {type === "job-submitted" && (
        <JobSubmittedContent onConfirm={onConfirm} amount={amount} />
      )}
    </Modal>
  );
};

export default AppModal;
